/**
 * useBankStateGuard.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Keeps completed bank extraction results alive regardless of sub-tab switches,
 * page navigation, or React re-renders.
 *
 * WHY THIS EXISTS
 * ───────────────
 * BankStatementWorkflow receives `bankSub` ("current" | "loan") as a prop from
 * ClientDashboard.  Every time the user clicks the sub-tab in the sidebar,
 * `bankSub` changes, which fires a useEffect([bankSub]) reset that wipes
 * `extracted` — destroying the download button even after a successful
 * extraction.  PersistentRoute keeps the component mounted across page
 * navigation, but it cannot stop a prop-triggered effect from clearing state.
 *
 * This hook stores extraction results in a MODULE-LEVEL object (_store) that
 * lives completely outside React's component lifecycle.  React prop changes,
 * re-renders, and effect cleanups cannot touch it.  Each sub-tab ("current"
 * and "loan") gets its own slot, so switching tabs shows the right result for
 * each without either one ever being lost.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * USAGE — drop-in replacement for the extracted useState in BankStatementWorkflow
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   import { useBankStateGuard } from './useBankStateGuard';
 *
 *   // Replace:
 *   const [extracted, setExtracted] = useState(null);
 *
 *   // With:
 *   const { result: extracted, saveResult, clearResult } = useBankStateGuard(bankSub);
 *
 *   // Then:
 *   saveResult(result)          instead of  setExtracted(result)
 *   clearResult()               instead of  setExtracted(null)
 *
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect, useRef } from 'react';

// ── Module-level store — never touched by React ───────────────────────────────
// Survives sub-tab switches, prop changes, PersistentRoute hide/show cycles,
// and everything else React does to the component tree.
const _store = {
  current: null,   // extraction result for Current Account tab
  loan:    null,   // extraction result for Loan Account tab
};

/**
 * @param {'current'|'loan'} bankSub  The active sub-tab key from props.
 * @returns {{ result, saveResult, clearResult }}
 */
export function useBankStateGuard(bankSub) {
  // Seed React state from the module store so the right result is shown
  // immediately on first render and on every bankSub change.
  const [result, _setResult] = useState(() => _store[bankSub] ?? null);

  // Always reflects the bankSub of the MOST RECENT render, regardless of
  // which render's closure happens to be calling saveResultToTab/
  // clearResultForTab below. Plain assignment (not useEffect) so it's
  // current even mid-render, before any effects have a chance to fire.
  const liveBankSubRef = useRef(bankSub);
  liveBankSubRef.current = bankSub;

  // When bankSub changes (user clicks Current ↔ Loan), sync React state from
  // the store.  This runs AFTER the bankSub reset effect in BankStatementWorkflow,
  // so even if that effect calls clearResult() for the wrong tab it doesn't matter —
  // this restores the correct value from the module store.
  useEffect(() => {
    _setResult(_store[bankSub] ?? null);
  }, [bankSub]);

  /** Save a completed extraction result for the current sub-tab. */
  const saveResult = (res) => {
    _store[bankSub] = res;
    _setResult(res);
  };

  /** Clear the result for the current sub-tab (e.g. "Extract Another" clicked). */
  const clearResult = () => {
    _store[bankSub] = null;
    _setResult(null);
  };

  /**
   * Save a result for an EXPLICIT tab key, regardless of which sub-tab is
   * currently active. Needed because an extraction started on "current" may
   * still be running after the user has switched to "loan" — the result must
   * land in the "current" slot, not wherever bankSub happens to point when
   * the request resolves.
   *
   * Only pushes into React state (_setResult) when the target tab is the one
   * currently being rendered; otherwise the module store is updated silently
   * and the bankSub-change effect above will pick it up the next time the
   * user switches back to that tab.
   */
  const saveResultToTab = (tab, res) => {
    _store[tab] = res;
    if (tab === liveBankSubRef.current) _setResult(res);
  };

  /** Clear the result for an EXPLICIT tab key — the inverse of saveResultToTab. */
  const clearResultForTab = (tab) => {
    _store[tab] = null;
    if (tab === liveBankSubRef.current) _setResult(null);
  };

  return { result, saveResult, clearResult, saveResultToTab, clearResultForTab };
}
